/**
 * Tenants List Page
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useTenants } from '../hooks';
import { format } from 'date-fns';
import type { TenantStatus, SubscriptionStatus } from '../types';

const statusColors: Record<TenantStatus, 'success' | 'warning' | 'error' | 'default'> = {
  PENDING: 'default',
  PROVISIONING: 'warning',
  ACTIVE: 'success',
  INACTIVE: 'error',
  FAILED: 'error',
};

const subscriptionColors: Record<SubscriptionStatus, 'success' | 'warning' | 'error' | 'default'> = {
  TRIAL: 'warning',
  ACTIVE: 'success',
  PAST_DUE: 'error',
  CANCELLED: 'error',
  SUSPENDED: 'error',
};

export function TenantsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useTenants();
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  const tenants = data?.tenants ?? [];
  const filteredTenants = tenants.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.slug.toLowerCase().includes(search.toLowerCase())
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, tenantId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedTenantId(tenantId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTenantId(null);
  };

  const handleViewTenant = () => {
    if (selectedTenantId) {
      navigate(`/tenants/${selectedTenantId}`);
    }
    handleMenuClose();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Tenants
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all tenants and their subscriptions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/tenants/new')}
        >
          Add Tenant
        </Button>
      </Box>

      <Card>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            placeholder="Search tenants..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Modules</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filteredTenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No tenants found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTenants.map((tenant) => (
                  <TableRow
                    key={tenant.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/tenants/${tenant.id}`)}
                  >
                    <TableCell>
                      <Typography fontWeight={500}>{tenant.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {tenant.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tenant.status}
                        size="small"
                        color={statusColors[tenant.status]}
                      />
                    </TableCell>
                    <TableCell>
                      {tenant.subscription ? (
                        <Chip
                          label={tenant.subscription.status}
                          size="small"
                          color={subscriptionColors[tenant.subscription.status]}
                          variant="outlined"
                        />
                      ) : (
                        <Chip label="No subscription" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {tenant.subscription?.modules?.length ?? 0} modules
                    </TableCell>
                    <TableCell>
                      {format(new Date(tenant.createdAt), 'dd.MM.yyyy')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, tenant.id);
                        }}
                      >
                        <MoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewTenant}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
      </Menu>
    </Box>
  );
}
